/**
 * The deferred-maintenance liability curve.
 *
 * The platform can say what will fail and roughly when. This says what it is
 * worth, and what it costs to not do it yet — which is the only form in which
 * maintenance wins a budget argument:
 *
 *     At 100,000 you fund one and defer one. Waiting a year on the deferred
 *     one costs 40,320 more than doing it now.
 *
 * THE RULE THAT MATTERS MOST HERE is that a candidate which cannot be priced
 * is not given a price. No default rate, no industry average, no silent zero.
 * It sits on the register with its cost stated as unknown, it is excluded from
 * every total, and the totals say how many were excluded — because a liability
 * figure that quietly omits a third of the register is worse than no figure.
 *
 * A screen therefore must never present a total without its coverage. The two
 * travel together in `LiabilityCurve` and the page renders them in one breath.
 */

/** One candidate that could not be priced, and why not. */
export interface Unpriced {
  title: string;
  whyNot: string;
}

/** What deferring one candidate costs, and what it does to the operation. */
export interface DeferredItem {
  title: string;
  escalation: number;
  /**
   * The operational consequence — closure hours — which is frequently not
   * knowable. `known: false` is a statement about the data, not a zero: a
   * candidate naming neither a zone nor an asset has nothing to attribute
   * closures to.
   */
  consequence: { known: boolean; whyNot?: string; closureHoursPerYear?: number };
}

/** One budget level, and what it buys. */
export interface Scenario {
  envelope: number;
  funded: number;
  fundedCost: number;
  deferred: number;
  deferredCostNow: number;
  /** The number that wins the argument. */
  costOfWaitingOneYear: number;
  /** Deferred candidates whose escalation could NOT be computed. */
  escalationNotComputed: number;
  closureHoursPerYear: number;
  closureKnownFor: number;
  closureUnknownFor: number;
  deferredDetail: DeferredItem[];
}

export interface LiabilityCurve {
  available: boolean;
  fiscalYear: number;
  currency: string;
  candidates: number;
  priced: number;
  notPriced: number;
  notPricedDetail: Unpriced[];
  /** The platform's own sentence about what the totals leave out. */
  coverageNote?: string;
  scenarios: Scenario[];
}

/** One row of the register, with the working that produced its number. */
export interface LiabilityRow {
  id: string;
  title: string;
  category?: string;
  status?: string;
  forecastFor?: string;
  priced: boolean;
  treatment?: string;
  currency?: string;
  costNow?: number;
  costDeferred?: number;
  escalation?: number;
  deferYears?: number;
  whyNot?: string;
  /**
   * Every figure carries the rate it used, when that rate was priced, the
   * quantity, and the deferral factor. A reader who disagrees can dispute an
   * input instead of dismissing the conclusion — which is the difference
   * between a number that survives a budget meeting and one that does not.
   */
  basis?: {
    unit?: string;
    rate?: string;
    pricedOn?: string;
    rateSource?: string;
    quantity?: string;
    factorPerYear?: string;
    deferralBasis?: string;
  };
}

export interface LiabilityRegister {
  candidates: number;
  priced: number;
  notPriced: number;
  note?: string;
  register: LiabilityRow[];
}

/**
 * What proportion of the register the totals actually cover.
 *
 * Returned as a fraction and never rounded up for presentation. A curve
 * covering a third of its candidates is a third of an answer, and the screen
 * should say so in the same breath as the money.
 */
export function coverage(c: { priced: number; candidates: number }): number | null {
  if (!c.candidates) return null;
  return c.priced / c.candidates;
}

/**
 * Whether the totals are complete enough to lead with.
 *
 * Deliberately strict. Anything under nine tenths priced and the coverage is
 * the headline, not a footnote — someone will screenshot the big number and
 * the caveat will not travel with it.
 */
export function totalsAreRepresentative(c: { priced: number; candidates: number }): boolean {
  const f = coverage(c);
  return f != null && f >= 0.9;
}

/** Scenarios worst-funded first: the argument runs from the tightest budget up. */
export function byEnvelope(scenarios: Scenario[]): Scenario[] {
  return [...scenarios].sort((a, b) => a.envelope - b.envelope);
}

/**
 * The sentence a budget holder acts on, or null when it cannot be said.
 *
 * Null rather than a zero-cost sentence: "waiting costs 0" reads as "waiting
 * is free", and the commonest reason for a zero here is that nothing could be
 * priced rather than that nothing escalates.
 */
export function costOfWaiting(s: Scenario): number | null {
  if (s.deferred === 0) return null;
  if (s.escalationNotComputed >= s.deferred) return null;
  return s.costOfWaitingOneYear;
}
